/**
 * Sign-in.
 *
 * The error message is deliberately identical for every failure the server
 * treats as a bad credential. Distinguishing "no such account" from "wrong
 * password" here would reintroduce, in the UI, the enumeration oracle the API
 * goes to some trouble to avoid.
 */

import { useState, type FormEvent } from 'react';
import { ApiError } from '../lib/api';
import { useSession } from '../lib/session';
import { Button } from '../components/ui';
import { AgencyMark, TricolourRule } from '../components/Brand';

export function SignIn() {
  const { signIn } = useSession();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      await signIn(email, password);
    } catch (caught) {
      if (caught instanceof ApiError) {
        // 5xx means our problem, not theirs — say so rather than implying the
        // credentials were wrong.
        setError(
          caught.status >= 500
            ? 'Sign-in is temporarily unavailable. Please try again shortly.'
            : (caught.problem?.detail ?? 'Email or password is incorrect.'),
        );
      } else {
        setError('Could not reach the server. Check your connection and try again.');
      }
      setSubmitting(false);
    }
  }

  const inputStyle = {
    borderRadius: 'var(--radius-sm)',
    borderColor: 'var(--color-rule-strong)',
    background: 'var(--color-surface)',
    color: 'var(--color-ink)',
  } as const;

  return (
    <main className="flex min-h-screen items-center justify-center px-4 py-10">
      <div className="animate-rise w-full max-w-sm">
        <header className="mb-6 text-center">
          <div className="flex justify-center">
            <AgencyMark size={52} />
          </div>
          <h1
            className="mt-3 text-md font-semibold tracking-tight"
            style={{ color: 'var(--color-ink)' }}
          >
            Land Transportation Office
          </h1>
          <p className="text-lg font-semibold tracking-tight" style={{ color: 'var(--color-ink)' }}>
            Confirmation System
          </p>
          <p className="mt-1 text-sm" style={{ color: 'var(--color-ink-subtle)' }}>
            Office-to-office correspondence and attachments
          </p>
        </header>

        <form
          onSubmit={handleSubmit}
          noValidate
          className="overflow-hidden border"
          style={{
            background: 'var(--color-surface)',
            borderColor: 'var(--color-rule)',
            borderRadius: 'var(--radius-lg)',
            boxShadow: 'var(--shadow-md)',
          }}
        >
          <TricolourRule />
          <div className="p-5">
          <div className="mb-3">
            <label htmlFor="email" className="mb-1 block text-sm font-medium">
              Email
            </label>
            <input
              id="email"
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="interactive w-full border px-2.5 py-2 text-base"
              style={inputStyle}
            />
          </div>

          <div className="mb-4">
            <label htmlFor="password" className="mb-1 block text-sm font-medium">
              Password
            </label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="interactive w-full border px-2.5 py-2 text-base"
              style={inputStyle}
            />
          </div>

          {error !== null && (
            <div
              role="alert"
              className="animate-rise mb-3 border px-2.5 py-2 text-sm"
              style={{
                borderRadius: 'var(--radius-sm)',
                background: 'var(--color-danger-subtle)',
                borderColor: 'var(--color-danger)',
                color: 'var(--color-danger)',
              }}
            >
              {error}
            </div>
          )}

          <Button type="submit" variant="primary" loading={submitting} style={{ width: '100%' }}>
            {submitting ? 'Signing in' : 'Sign in'}
          </Button>
          </div>
        </form>

        <p className="mt-4 text-center text-xs" style={{ color: 'var(--color-ink-subtle)' }}>
          Authorised users only. Access is logged.
        </p>
      </div>
    </main>
  );
}
